/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import ButtonWithIcon from "@/components/atoms/button-with-icon";
import Button, { buttonVariants } from "@/components/atoms/buttons/Button";
import ConfirmDeleteModal from "@/components/atoms/confirm-delete-modal";
import { Toast, toast } from "@/components/atoms/toast";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import WishlistOrderSummaryFooter from "@/components/modules/account/wishlist-order-summary-footer";
import WishlistProductCard from "@/components/modules/account/wishlist-product-card";
import { Input } from "@/components/ui/input";
import { useCart } from "@/hooks/useCart";
import { useAddToCart, useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { usePrismicProducts, useProducts } from "@/hooks/useProduct";
import { useWishlist } from "@/hooks/useWishlist";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { buildWishlistCartPlan } from "@/lib/wishlist-cart-plan";
import { useCheckoutStore } from "@/stores/checkout-store";
import { PencilIcon, CheckIcon, XIcon, Loader2, ShoppingCart } from "lucide-react";
import Link from "next/link";
import { useMemo, useRef, useState } from "react";

type AccountWishlistDetailsProps = {
  locale: Locales;
};

const AccountWishlistDetails = ({ locale }: AccountWishlistDetailsProps) => {
  const {
    lists,
    deleteList,
    renameList,
    activeListName,
    isEditing,
    editedName,
    setIsEditing,
    setEditedName,
    isLoading,
  } = useWishlist();

  const { cart } = useCart();

  const productCustomizations = useCheckoutStore((state) => state.productCustomizations);

  const quantitiesRef = useRef<Record<string, number>>({});
  const updateProductQuantity = (productId: string, quantity: number) => {
    quantitiesRef.current[productId] = quantity;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const activeList = activeListName ? lists?.find((list) => list.listName === activeListName) : lists![0];
  const products = activeList?.products || [];
  const productIds = products.map((p) => p.productId);

  const { data: prismicProducts, isLoading: prismicLoading } = usePrismicProducts(
    locale,
    productIds,
    {},
    productIds.length > 0
  );
  const { data: medusaProducts, isLoading: medusaLoading } = useProducts(locale, productIds, productIds.length > 0);
  const productsArray = Object.values(medusaProducts || {});

  const { addToCart, isLoading: isAddingAllToCart } = useAddToCart({
    onError: (error) => {
      console.error("Failed to add item to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });
  const { removeItem } = useRemoveCartItem({});
  const { updateQuantity } = useUpdateCartItem({
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  const totalPages = Math.ceil(products.length / itemsPerPage);

  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return products.slice(startIndex, startIndex + itemsPerPage);
  }, [products, currentPage]);

  const handleStartEditing = () => {
    if (activeList?.listName) {
      setEditedName(activeList.listName);
      setIsEditing(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedName("");
  };

  const handleSaveRename = async () => {
    if (editedName.trim() && activeList?.id) {
      try {
        await renameList(activeList.id, editedName.trim());
        handleCancelEditing();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : typeof error === "string" ? error : "Something went wrong";
        toast.error({ title: message });
      }
    }
  };

  if (isLoading || !lists) return;

  if (!activeList) {
    return (
      <div className="py-10 text-center text-lg text-gray-500">{__("You have not created any list yet.", locale)}</div>
    );
  }

  const loading = prismicLoading || medusaLoading;
  const rangeProductIds = productsArray.filter((p) => p?.metadata?.is_surface_based).map((p) => p.id);
  const allRangeConfigured = rangeProductIds.every((id) => !!productCustomizations[id]);

  const unconfiguredMedusaRangeProducts = Object.fromEntries(
    Object.entries(medusaProducts || {}).filter(([id, p]) => {
      return p?.metadata?.is_surface_based && !productCustomizations[id];
    })
  );

  const unconfiguredPrismicRangeProducts =
    prismicProducts?.filter((pp) => {
      const medusaId = pp?.data?.medusaId;
      return medusaId && medusaProducts?.[medusaId]?.metadata?.is_surface_based && !productCustomizations[medusaId];
    }) || [];

  const isAllProductsOutOfStock = productsArray.every((product) => product.status === "draft");

  const isProductsInCart = productsArray
    .filter((product) => product.status !== "draft")
    .every((product) => {
      const variants = product.variants || [];
      if (variants.length > 1) {
        // Derive the currently-selected variant from the store directly — same
        // logic as useProductVariant — instead of reading from variantGettersRef.
        //
        // variantGettersRef holds closures from children's last render. When the
        // parent re-renders first (e.g. on cart change), children haven't re-run
        // their effects yet, so the ref still holds the OLD getter → stale variant id.
        //
        // productCustomizations is a Zustand selector — reactive, always up to date
        // at the moment this render runs.
        const storedSurface = productCustomizations[product.id]?.surface;
        const selectedVariant = variants.find((v) => v.metadata?.["surface"] == storedSurface) ?? variants[0];
        if (!selectedVariant?.id) return false;
        return cart?.items?.some(
          (item) =>
            item.variant_id === selectedVariant.id &&
            activeList?.id &&
            Array.isArray(item.metadata?.wishlist_ids) &&
            item.metadata.wishlist_ids.includes(activeList.id)
        );
      }
      // Single-variant: straightforward
      const variantId = variants[0]?.id;
      const cartItem = cart?.items?.find((item) => item.variant_id === variantId);
      return (
        cartItem &&
        activeList?.id &&
        Array.isArray(cartItem.metadata?.wishlist_ids) &&
        cartItem.metadata.wishlist_ids.includes(activeList.id)
      );
    });

  const handleAddAll = async () => {
    try {
      const plan = buildWishlistCartPlan({
        products: productsArray,
        wishlistItems: activeList.products,
        quantityOverrides: quantitiesRef.current,
        customizations: productCustomizations,
        cartVariantIds: cart?.items?.flatMap((item) => (item.variant_id ? [item.variant_id] : [])),
      });

      if (plan.kind === "blocked") {
        console.error("Wishlist contains products without a variant", plan.productIds);
        toast.error({ title: __("Failed to add some products to cart", locale) });
        return;
      }

      const promises = plan.items.map(({ variantId, variantIds, quantity }) => {
        const existingItem = cart?.items?.find((i) => i.variant_id === variantId);

        if (existingItem) {
          const existingIds = Array.isArray(existingItem.metadata?.wishlist_ids)
            ? (existingItem.metadata.wishlist_ids as string[])
            : [];
          const mergedIds = existingIds.includes(activeList.id) ? existingIds : [...existingIds, activeList.id];
          return updateQuantity(existingItem.id, existingItem.quantity + quantity, {
            wishlist_ids: mergedIds,
          });
        }

        if (variantIds.length > 1) {
          const otherItem = cart?.items?.find(
            (i) => i.variant_id !== variantId && variantIds.includes(i.variant_id ?? "")
          );
          if (otherItem) removeItem(otherItem.id);
        }

        return addToCart(variantId, quantity, {
          wishlist_ids: [activeList.id],
        });
      });

      await Promise.all(promises);
      toast.success({ title: __("All products added to cart!", locale) });
    } catch (err) {
      console.error("Error adding all items to cart:", err);
      toast.error({ title: __("Failed to add some products to cart", locale) });
    }
  };

  return (
    <article className="flex flex-1/2 flex-col gap-5 max-md:p-1.5">
      <section className="flex items-start justify-between">
        <div className="flex flex-col gap-1 space-y-0.5">
          {isEditing ? (
            <div className="flex items-center gap-2 border-b">
              <Input
                className={cn(
                  "font-cabinet-grotesk focus-visible:ring-none min-w-min border-none px-0 text-2xl font-medium text-black shadow-none focus-visible:border-none focus-visible:ring-[0px] md:text-3xl",
                  isEditing && "border"
                )}
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                autoFocus
              />
              <button
                className="cursor-pointer disabled:cursor-not-allowed"
                onClick={handleSaveRename}
                disabled={editedName.trim() === activeList.listName.trim()}
              >
                <CheckIcon size={20} className="text-green-500" />
              </button>
              <button className="cursor-pointer" onClick={handleCancelEditing}>
                <XIcon size={20} className="text-red-500" />
              </button>
            </div>
          ) : (
            <h2 className="font-cabinet-grotesk flex items-center gap-3 text-2xl font-medium text-black md:text-3xl">
              {activeList.listName}
              <button className="cursor-pointer" onClick={handleStartEditing}>
                <PencilIcon className="text-primary-light" size={24} />
              </button>
            </h2>
          )}
          <p className="text-primary-light">
            {activeList?.date?.from} - {activeList?.date?.to}
          </p>
        </div>

        <ConfirmDeleteModal
          modalTrigger={
            <ButtonWithIcon
              iconLeftName="Trash2"
              size="md"
              className="border-primary-light/50 font-cabinet-grotesk h-11 bg-inherit max-sm:hidden"
              variant="secondary"
            >
              {__("Delete the list", locale)}
            </ButtonWithIcon>
          }
          title={__("Delete the list", locale)}
          text={__("Are you sure you want to delete this list?", locale)}
          onClick={() => deleteList(activeList.id)}
          locale={locale}
        />
      </section>

      {products.length === 0 ? (
        <div className="py-10 text-center text-lg text-gray-500">
          {__("You have not added any items in this list.", locale)}
        </div>
      ) : loading ? (
        <div className="py-10 text-center text-lg text-gray-500">{__("Loading products...", locale)}</div>
      ) : (
        <>
          <ul className="space-y-2.5" key={activeList?.id}>
            {paginatedProducts.map((product) => {
              const prismicProduct = prismicProducts?.find((p) => p?.data.medusaId === product.productId);
              const medusaProduct =
                prismicProduct && prismicProduct.uid ? medusaProducts?.[prismicProduct.data.medusaId!] : undefined;
              const wishlistItem = activeList?.products?.find((p) => p.productId === medusaProduct?.id);

              return medusaProduct && prismicProduct ? (
                <WishlistProductCard
                  isRange={!!medusaProduct?.metadata?.is_surface_based}
                  key={`${prismicProduct.uid}-${activeList.id}-${wishlistItem?.id}-${wishlistItem?.quantity}`}
                  locale={locale}
                  product={prismicProduct}
                  medusaProduct={medusaProduct}
                  onQuantityChange={(quantity) => updateProductQuantity(medusaProduct.id, quantity)}
                  initialQuantity={wishlistItem?.quantity}
                />
              ) : null;
            })}
          </ul>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-50"
              >
                &laquo;
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-50"
              >
                &lsaquo;
              </button>
              <span className="flex h-9 w-9 items-center justify-center rounded bg-black font-medium text-white">
                {currentPage}
              </span>
              <span>/</span>
              <span className="flex h-9 w-9 items-center justify-center rounded bg-black font-medium text-white">
                {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-50"
              >
                &rsaquo;
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded border disabled:cursor-not-allowed disabled:opacity-50"
              >
                &raquo;
              </button>
            </div>
          )}
        </>
      )}

      {!allRangeConfigured && (
        <>
          <hr className="border-pink-light/50" />
          <Toast
            className="max-w-none shadow-none"
            type="error"
            title={__("Some items are not yet configured, you need to configure them to be able to order", locale)}
            id="Wishlist Products"
          />
        </>
      )}

      <WishlistOrderSummaryFooter locale={locale} medusaProducts={productsArray} />

      {!allRangeConfigured && (
        <ProductConfigurationModal
          medusaProducts={unconfiguredMedusaRangeProducts}
          products={unconfiguredPrismicRangeProducts}
          locale={locale}
          triggerClassName="min-h-10 sm:ml-auto"
        />
      )}

      {!loading &&
        allRangeConfigured &&
        productsArray.length !== 0 &&
        (isAllProductsOutOfStock ? (
          <Toast
            id="collection-out-of-stock"
            type="info"
            className="mt-3 max-w-none shadow-none"
            title={__("All products are out of stock", locale)}
          />
        ) : isProductsInCart ? (
          <div className="bg-product-card flex w-full items-center gap-3 rounded-sm p-2 max-sm:flex-col">
            <div className="flex w-full items-center gap-3 max-sm:items-start">
              <ShoppingCart
                size={14}
                className="size-5 shrink-0 text-green-600 max-sm:mt-1 md:size-5"
                aria-hidden="true"
              />
              <span className="font-medium text-green-600">{__("All items added to cart !", locale)}</span>
            </div>
            <Link
              href={getPermalink(locale, "cart")}
              aria-label={__("Go to cart", locale)}
              className={cn(
                buttonVariants(),
                "flex h-10 items-center justify-center rounded-xs p-0 px-2 text-sm transition-transform duration-150 ease-in-out active:scale-95 max-sm:w-full md:px-4 md:text-base"
              )}
            >
              {__("View Cart", locale)}
            </Link>
          </div>
        ) : (
          <Button
            disabled={isAddingAllToCart}
            onClick={handleAddAll}
            variant="primary"
            className="h-11 text-base font-semibold min-sm:ml-auto min-sm:w-fit"
            size="md"
          >
            {isAddingAllToCart ? <Loader2 className="animate-spin" /> : <ShoppingCart aria-hidden="true" />}
            {__("Add all products to cart", locale)}
          </Button>
        ))}
    </article>
  );
};

export default AccountWishlistDetails;
